"use client";

import Link from "next/link";
import { ArrowRight, Mail, Send, Newspaper, Check } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { aboutContact as data } from "@/lib/data/about";

const methodIcons = { Mail, Send, Newspaper } as const;
const methodAccents = {
  "General Inquiries": "var(--color-cyan)",
  "Sales & Demos": "var(--color-crimson)",
  "Media & Press": "var(--color-purple)",
} as const;

export function AboutContact() {
  return (
    <Section id="contact" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/4 h-96 w-96 rounded-full bg-[var(--color-crimson)] opacity-25 blur-[140px]" />
        <div className="absolute bottom-1/4 left-1/4 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-20 blur-[120px]" style={{ animationDelay: "2s" }} />
      </div>

      <Container>
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10">
          <div>
            <Reveal>
              <span className="eyebrow-tiny text-[var(--color-crimson)]">{data.eyebrow}</span>
              <h2 className="mt-3">{data.title}</h2>
              <p className="mt-5 text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-xl">{data.intro}</p>
            </Reveal>

            <Reveal delay={120}>
              <ul className="mt-8 space-y-3">
                {data.promises.map((p, i) => (
                  <li key={p} className="flex items-center gap-3 text-sm">
                    <span
                      className="grid place-items-center h-7 w-7 rounded-full shrink-0"
                      style={{
                        background: "color-mix(in oklch, var(--color-emerald) 18%, transparent)",
                        boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-emerald) 35%, transparent)",
                      }}
                    >
                      <Check className="h-3.5 w-3.5 text-[var(--color-emerald)]" />
                    </span>
                    <span className="text-[rgb(var(--fg-muted))]">{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={200}>
              <div className="mt-10 glass rounded-2xl p-6">
                <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Next Steps</div>
                <ol className="mt-5 space-y-4">
                  {data.nextSteps.map((s) => (
                    <li key={s.number} className="flex items-start gap-3">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-full shrink-0 font-bold text-sm"
                        style={{
                          background: "color-mix(in oklch, var(--color-crimson) 18%, transparent)",
                          color: "var(--color-crimson)",
                          boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-crimson) 35%, transparent)",
                        }}
                      >
                        {s.number}
                      </span>
                      <div>
                        <div className="font-semibold text-sm">{s.title}</div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{s.body}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>

          <Reveal delay={140}>
            <div className="glass rounded-2xl p-7 h-fit">
              <div className="eyebrow-tiny text-[var(--color-cyan)]">Get in Touch</div>
              <h3 className="mt-2 text-xl">Direct Lines</h3>
              <div className="mt-6 space-y-3">
                {data.contactMethods.map((m, i) => {
                  const Icon = (methodIcons[Object.keys(methodIcons)[i % Object.keys(methodIcons).length] as keyof typeof methodIcons] ?? Mail);
                  const accent = methodAccents[m.label as keyof typeof methodAccents] ?? "var(--color-cyan)";
                  return (
                    <a
                      key={m.label}
                      href={`mailto:${m.email}`}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-[rgb(var(--border)/0.04)] transition-colors group"
                    >
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                        style={{
                          background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: accent }} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="eyebrow-tiny font-semibold" style={{ color: accent }}>
                          {m.label}
                        </div>
                        <div className="text-sm font-semibold mt-0.5 group-hover:underline" style={{ color: accent }}>
                          {m.email}
                        </div>
                        {m.note && (
                          <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5">{m.note}</div>
                        )}
                      </div>
                    </a>
                  );
                })}
              </div>

              <div className="mt-6 pt-6 border-t border-[rgb(var(--border)/0.12)]">
                <Link
                  href="/#contact-form"
                  className="btn-primary-gradient w-full h-11 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm"
                >
                  Request a Demo <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}
